import React from "react";
import striptags from "striptags";
import HeadingView from "common/views/heading/Heading.view";
import { ImageVariant, ImageView } from "common/views/image/Image.view";
import TypographyView from "common/views/typographyView/Typography.view";
import { IDataFragment } from "core/components/FragmentProvider";
import IKBFragmentProps from "./IKBFragementProps";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import KbLabels from "panels/knowledgebase/Kb.labels";
import ConfigUtil from "core/util/config/ConfigUtil";
import PageLinkView from "common/views/pageLink/PageLink.view";
import { IBreadcrumb } from "core/util/IBreadcrumb";
import { lastItem } from "core/util/ArrayUtil";
import UrlUtil from "core/util/UrlUtil";

// title
export const title: IDataFragment<IKBFragmentProps> = {
	name: "Title",
	label: { k: "article.title", v: "Title" },
	component: ({ article }) => {
		return (
			<HeadingView viewKey="article.title">
				{article && article.title}
			</HeadingView>
		);
	},
};

// description
export const displayName: IDataFragment<IKBFragmentProps> = {
	name: "displayName",
	label: { k: "article.displayName", v: "Description" },
	component: ({ article }) => (
		<TypographyView viewKey="article.displayName">
			{article && article.displayName}
		</TypographyView>
	),
};

// description
export const description: IDataFragment<IKBFragmentProps> = {
	name: "Description (sanitized)",
	label: { k: "article.descriptionInfo", v: "Description" },
	component: ({ article }) => (
		<TypographyView viewKey="article.descriptionInfo">
			{article && striptags(article.description)}
		</TypographyView>
	),
};

// description
export const descriptionHtml: IDataFragment<IKBFragmentProps> = {
	name: "Description (html)",
	label: { k: "article.descriptionInfo", v: "Description" },
	component: ({ article }) => (
		<div className="html-default">
			<TypographyView
				viewKey="article.descriptionInfo"
				html={article && article.description}
			/>
		</div>
	),
};

// image
export const image: IDataFragment<IKBFragmentProps> = {
	name: "Static image",
	label: { k: "article.iconUrl", v: "Image" },
	component: ({ article }) => (
		<ImageView
			style={{
				display: "block",
				maxWidth: "100%",
				width: "100%",
			}}
			src={article && article.iconUrl}
			variant={ImageVariant.THUMBNAIL}
			viewKey="article.iconUrl.static"
			overlay={
				article &&
				article.iconUrl && {
					src: article.iconUrl,
					position: "top left",
				}
			}
		/>
	),
};

export const openArticle: IDataFragment<IKBFragmentProps> = {
	name: "Open",
	label: { k: "article.viewDetails", v: "Open" },
	component: ({ article, params, knowledgeBase }) => {
		const knowledgeBaseId = (knowledgeBase && knowledgeBase.id) || "";
		const articleId = (article && article.id) || "";
		const categoryId =
			(article && article.categories && article.categories[0].id) || "";

		const appPath = ConfigUtil.getEnvConfig().appPath;
		let path = "";
		if (appPath) {
			path =
				appPath +
				`article?articleId=${articleId}&categoryId=${categoryId}&knowledgeBaseId=${knowledgeBaseId}`;
		}

		return (
			<PageLinkView
				attrs={
					article && {
						"data-wap_ref": `${article.id}:${article.title}`,
					}
				}
				params={{
					articleId: articleId,
					categoryId: categoryId,
					knowledgeBaseId: knowledgeBaseId,
				}}
				viewKey="article.detailsLink.link"
				breadcrumbs={(target, breadcrumbs) => {
					let nextCrumbs: IBreadcrumb[] = [];
					const articleKey = "article:" + article.id;
					// push current page's crumb
					const lastCrumb = lastItem(breadcrumbs);

					const currentPath = UrlUtil.getCurrentPath();
					// if last crumb was for the current page and has a target, use that one
					if (
						lastCrumb &&
						lastCrumb.target.pathname === currentPath
					) {
						nextCrumbs = [
							...breadcrumbs.slice(0, breadcrumbs.length - 1),
							{
								...lastCrumb,
								target: {
									pathname: currentPath,
									search: UrlUtil.getSearch(),
								},
							},
						];
					} else if (
						// if last crumb was for the current page and has no target path associated
						lastCrumb &&
						lastCrumb.target.pathname === ""
					) {
						nextCrumbs = [
							...breadcrumbs.slice(0, breadcrumbs.length - 1),
							{
								...lastCrumb,
								target: {
									pathname: currentPath,
									search: UrlUtil.getSearch(),
								},
							},
						];
					} else {
						// else create a new one for the current page
						nextCrumbs = [
							...breadcrumbs,
							{
								key: "article",
								label: {
									k: articleKey,
									v:
										window.parent.location.pathname.slice(
											1
										) || "Home",
								},
								target: {
									pathname: currentPath,
									search: UrlUtil.getSearch(),
								},
							},
						];
					}

					// push article crumb
					nextCrumbs.push({
						key: articleKey,
						label: {
							k: articleKey,
							v: article.title,
						},
						//target
						target: {
							pathname: path,
							search: "",
						},
					});

					nextCrumbs.push({
						key: articleKey,
						label: {
							k: articleKey,
							v: article.title,
						},
						//target
						target: {
							pathname: path,
							search: "",
						},
					});

					const category = {
						key: "KbCategory:" + article.categories[0].id,
						label: {
							k: "KbCategory:" + article.categories[0].id,
							v: article.categories[0].name,
						},
						//target
						target: {
							pathname: nextCrumbs[0].target.pathname,
							search: nextCrumbs[0].target.search,
						},
					};

					nextCrumbs = [category, nextCrumbs[1]];
					return nextCrumbs;
				}}
			>
				<ButtonView
					variant={ButtonVariant.PRIMARY}
					viewKey="article.viewDetails.button"
				>
					<KbLabels.Labels.ViewDetails />
				</ButtonView>
			</PageLinkView>
		);
	},
};



// WEBPACK FOOTER //
// ./src/panels/knowledgebase/fragments/article/displayFragments.tsx